import { Check, CreditCard } from 'lucide-react';
import { useAuthStore } from '@/stores/auth.store';
import { useBillingSummary, useCheckout } from './billing.api';

const STATUS_LABEL: Record<string, { label: string; cls: string }> = {
  none: { label: 'Sin suscripción', cls: 'badge-gray' },
  trial: { label: 'Prueba', cls: 'badge-amber' },
  active: { label: 'Activa', cls: 'badge-green' },
  past_due: { label: 'Pago pendiente', cls: 'badge-amber' },
  suspended: { label: 'Suspendida', cls: 'badge-red' },
  cancelled: { label: 'Cancelada', cls: 'badge-red' },
};

const PLANS = [
  { name: 'Básico', price: 99, feats: ['1 empresa', 'POS + Comprobantes', 'Hasta 500 comprobantes/mes', '1 usuario'] },
  { name: 'Pro', price: 199, feats: ['3 empresas', 'Todo en Básico', 'Inventario + Finanzas', '5 usuarios', 'Reportes PLE'] },
  { name: 'Premium', price: 249, feats: ['Empresas ilimitadas', 'Todo en Pro', 'CRM + Marketing', 'Usuarios ilimitados', 'Soporte prioritario'] },
];

function money(n: number, currency: string): string {
  return new Intl.NumberFormat('es-PE', { style: 'currency', currency }).format(n);
}

export function BillingTab(): JSX.Element {
  const { data, isLoading } = useBillingSummary();
  const checkout = useCheckout();

  const user = useAuthStore((s) => s.user);
  const activeTenantId = useAuthStore((s) => s.activeTenantId);
  const role = user?.memberships.find((m) => m.tenantId === activeTenantId)?.role;
  const isOwner = role === 'owner' || role === 'superadmin';

  if (isLoading || !data) {
    return (
      <div className="cx">
        <p className="muted t-sm">Cargando suscripción…</p>
      </div>
    );
  }

  const st = STATUS_LABEL[data.status] ?? STATUS_LABEL.none!;
  const isCur = (p: (typeof PLANS)[number]): boolean =>
    (data.planName ?? '').toLowerCase().includes(p.name.toLowerCase()) || Number(data.planPrice) === p.price;

  return (
    <div className="cx">
      <div className="row aic gap10" style={{ marginBottom: 16 }}>
        <span className="muted t-sm">Estado de tu suscripción:</span>
        <span className={`badge ${st.cls}`}>
          <span className="bdot" />
          {st.label}
        </span>
      </div>

      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 16, maxWidth: 920 }}>
        {PLANS.map((p) => {
          const cur = isCur(p);
          return (
            <div
              key={p.name}
              className="card card-pad"
              style={{
                position: 'relative',
                border: cur ? '1.5px solid var(--primary)' : '1px solid var(--border)',
                boxShadow: cur ? '0 8px 28px -10px rgba(120,210,240,.6)' : 'var(--sh-sm)',
              }}
            >
              {cur && (
                <span className="badge badge-yellow" style={{ position: 'absolute', top: 16, right: 16 }}>
                  Plan actual
                </span>
              )}
              <div className="t-h2 fw7">{p.name}</div>
              <div className="row aie gap4" style={{ margin: '10px 0 18px' }}>
                <span className="t-display tnum fw8">S/{p.price}</span>
                <span className="muted" style={{ marginBottom: 6 }}>
                  /mes
                </span>
              </div>
              <div className="col gap10" style={{ marginBottom: 20 }}>
                {p.feats.map((f) => (
                  <div key={f} className="row aic gap8 t-sm">
                    <span style={{ color: '#22C55E' }}>
                      <Check size={16} />
                    </span>
                    {f}
                  </div>
                ))}
              </div>
              <button
                className={`btn btn-block ${cur ? 'btn-secondary' : 'btn-primary'}`}
                disabled={cur || !isOwner || checkout.isPending}
                onClick={() => !cur && checkout.mutate()}
              >
                {cur ? 'Plan activo' : `Cambiar a ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="card card-pad row between aic wrap" style={{ maxWidth: 920, marginTop: 16, gap: 12 }}>
        <div className="row aic gap12">
          <div style={{ width: 40, height: 40, borderRadius: 11, background: '#E0F2FE', color: '#0369A1', display: 'grid', placeItems: 'center', flex: 'none' }}>
            <CreditCard size={20} />
          </div>
          <div>
            <div className="fw6 t-sm">Mercado Pago</div>
            <div className="t-xs muted">Próximo cargo: {money(data.total, data.currency)} · {data.planName}</div>
          </div>
        </div>
        <button className="btn btn-secondary btn-sm" disabled={!isOwner || checkout.isPending} onClick={() => checkout.mutate()}>
          {checkout.isPending ? 'Redirigiendo…' : 'Cambiar método'}
        </button>
      </div>

      {!isOwner && (
        <p className="t-xs muted" style={{ marginTop: 12 }}>
          Solo el owner puede gestionar el pago de la suscripción.
        </p>
      )}
      <p className="t-xs muted" style={{ marginTop: 8 }}>
        El pago se procesa de forma segura en Mercado Pago.
      </p>
    </div>
  );
}
